import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  FC,
  ReactNode,
} from 'react'

export type PricePerSqmBasis = 'living' | 'usable' | 'weighted'

export interface AreaMap {
  livingArea?: number
  usableArea?: number
  weightedArea?: number
}

interface PublicPricePerSqmState {
  show: boolean
  basis: PricePerSqmBasis
  effectiveBasis: PricePerSqmBasis | null
  setShow: (v: boolean) => void
  setBasis: (v: PricePerSqmBasis) => void
  resolveArea: (areas: AreaMap) => number | undefined
  areas: AreaMap
}

const FALLBACK_ORDER: PricePerSqmBasis[] = ['living', 'usable', 'weighted']
const basisToField: Record<PricePerSqmBasis, keyof AreaMap> = {
  living: 'livingArea',
  usable: 'usableArea',
  weighted: 'weightedArea',
}

const resolveBasis = (
  areas: AreaMap,
  preferred: PricePerSqmBasis
): PricePerSqmBasis | null => {
  const preferredValue = areas[basisToField[preferred]]
  if (preferredValue && preferredValue > 0) return preferred

  for (const candidate of FALLBACK_ORDER) {
    if (candidate === preferred) continue
    const value = areas[basisToField[candidate]]
    if (value && value > 0) return candidate
  }

  return null
}

const PublicPricePerSqmContext = createContext<PublicPricePerSqmState | null>(
  null
)

interface PublicPricePerSqmProviderProps {
  areas: AreaMap
  children: ReactNode
  /**
   * Optional initial state for future snapshot hydration (e.g. embedding the
   * preference in a share-link payload). Session-only — not persisted.
   */
  initial?: { show?: boolean; basis?: PricePerSqmBasis }
}

export const PublicPricePerSqmProvider: FC<PublicPricePerSqmProviderProps> = ({
  areas,
  children,
  initial,
}) => {
  const [show, setShowState] = useState<boolean>(initial?.show ?? true)
  const [basis, setBasisState] = useState<PricePerSqmBasis>(
    initial?.basis ?? 'living'
  )

  const setShow = useCallback((v: boolean) => {
    setShowState(v)
  }, [])

  const setBasis = useCallback((v: PricePerSqmBasis) => {
    setBasisState(v)
  }, [])

  const resolveArea = useCallback(
    (areaMap: AreaMap): number | undefined => {
      const effective = resolveBasis(areaMap, basis)
      if (!effective) return undefined
      return areaMap[basisToField[effective]]
    },
    [basis]
  )

  const effectiveBasis = useMemo(
    () => resolveBasis(areas, basis),
    [areas, basis]
  )

  const value = useMemo<PublicPricePerSqmState>(
    () => ({
      show,
      basis,
      effectiveBasis,
      setShow,
      setBasis,
      resolveArea,
      areas,
    }),
    [show, basis, effectiveBasis, setShow, setBasis, resolveArea, areas]
  )

  return (
    <PublicPricePerSqmContext.Provider value={value}>
      {children}
    </PublicPricePerSqmContext.Provider>
  )
}

export const usePublicPricePerSqm = (): PublicPricePerSqmState => {
  const ctx = useContext(PublicPricePerSqmContext)
  if (!ctx) {
    throw new Error(
      'usePublicPricePerSqm must be used within a PublicPricePerSqmProvider'
    )
  }
  return ctx
}
